import { useId, type ReactNode } from 'react';
import { Button, Column, Dialog, DialogActions, DialogContent, Typography } from '@tempmee/react';
import { OutlineXCircleErrorIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';

// DS Dialog sizes are 250/620/800px; the Figma dialog is 358px.
const PANEL_STYLE = { maxWidth: 358 } as const;

const ICON_SIZE = 80;

export interface ICancelShiftDialogFrameProps {
  title: ReactNode;
  /** The sentence the dialog is described by; `children` are not part of the description. */
  description: ReactNode;
  /** Extra content under the description, e.g. the refund breakdown. */
  children?: ReactNode;
  confirmLabel: string;
  isPending?: boolean;
  /** Also runs on Escape. */
  onClose: () => void;
  onConfirm: () => void;
  'data-cy': string;
  goBackDataCy: string;
  confirmDataCy: string;
}

/** The v3 cancel dialog chrome (GOTU-15983) shared by the confirm and refund dialogs. */
export const CancelShiftDialogFrame = ({
  title,
  description,
  children,
  confirmLabel,
  isPending = false,
  onClose,
  onConfirm,
  'data-cy': dataCy,
  goBackDataCy,
  confirmDataCy,
}: ICancelShiftDialogFrameProps) => {
  // Heading sits in the content: a string DialogTitle is whitespace-nowrap and overflows 358px.
  const titleId = useId();
  const descriptionId = useId();

  return (
    <Dialog
      open
      onClose={onClose}
      size="medium"
      style={PANEL_STYLE}
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      data-cy={dataCy}
    >
      <DialogContent>
        <Column gap={4} align="center" pt={4}>
          <OutlineXCircleErrorIcon size={ICON_SIZE} color={colors.primary[500]} />
          <Typography id={titleId} variant="h6" color={colors.text.primary} textAlign="center">
            {title}
          </Typography>
          <Typography
            id={descriptionId}
            variant="body1"
            color={colors.text.secondary}
            textAlign="center"
          >
            {description}
          </Typography>
          {children}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          disabled={isPending}
          onClick={onClose}
          data-cy={goBackDataCy}
        >
          Go Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={isPending}
          onClick={onConfirm}
          data-cy={confirmDataCy}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
